/**
 * AuthModal.tsx
 * Handles login, registration, and Google OAuth.
 * After Google sign-in, if no vendor exists yet, shows setup step
 * to collect business name + WhatsApp number.
 */

import React, { useState } from 'react';
import { X, User, LogIn, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Vendor } from '../types';

interface AuthModalProps {
  mode: 'login' | 'register';
  onClose: () => void;
  onSuccess: (vendor: Vendor) => void;
}

// Google "G" SVG icon
function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

export default function AuthModal({ mode: initialMode, onClose, onSuccess }: AuthModalProps) {
  const [mode, setMode]         = useState<'login' | 'register'>(initialMode);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [whatsapp, setWhatsapp]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState<string | null>(null);

  // ── Google OAuth ────────────────────────────────────────────────────────────
  const handleGoogleAuth = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw new Error(error.message);
      // Page will redirect to Google — no further action needed here
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed.');
      setGoogleLoading(false);
    }
  };

  // ── Register ────────────────────────────────────────────────────────────────
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const slug = businessName.toLowerCase().replace(/[^a-z0-9]/g, '').trim();
    if (!slug) { setError('Business name must contain letters or numbers.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

    let cleanPhone = whatsapp.replace(/[^0-9]/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = '233' + cleanPhone.substring(1);
    else if (!cleanPhone.startsWith('233') && cleanPhone.length === 9) cleanPhone = '233' + cleanPhone;
    if (cleanPhone.length < 9 || cleanPhone.length > 15) {
      setError('Please enter a valid WhatsApp number (e.g. 0244123456).');
      return;
    }

    setLoading(true);
    try {
      const { data: authData, error: authErr } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
      });
      if (authErr) throw new Error(authErr.message);
      if (!authData.user) throw new Error('Account creation failed — please try again.');

      const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vendors`;
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          business_name: slug,
          email: email.trim().toLowerCase(),
          whatsapp_number: cleanPhone,
          auth_user_id: authData.user.id,
        }),
      });

      const body = await res.json();
      if (!res.ok) {
        await supabase.auth.signOut();
        throw new Error(body.error || 'Vendor profile creation failed.');
      }
      onSuccess(body);
    } catch (err: any) {
      setError(err.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  // ── Login ────────────────────────────────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data, error: signInErr } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (signInErr) throw new Error('Incorrect email or password.');
      if (!data.user) throw new Error('Login failed — please try again.');

      const { data: vendor, error: vendorErr } = await supabase
        .from('vendors')
        .select('*')
        .eq('email', data.user.email!)
        .single();

      if (vendorErr || !vendor) throw new Error('No vendor account found for this email.');
      onSuccess(vendor);
    } catch (err: any) {
      setError(err.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password ──────────────────────────────────────────────────────────
  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Enter your email address above first, then click Forgot password.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        { redirectTo: `${window.location.origin}/reset-password` }
      );
      if (resetErr) throw new Error(resetErr.message);
      setSuccess(`Reset email sent to ${email.trim()}. Check your inbox.`);
    } catch (err: any) {
      setError(err.message || 'Could not send reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 w-full max-w-md p-7 relative max-h-[90dvh] overflow-y-auto">

        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-slate-100 text-slate-400 transition-colors">
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-2.5 mb-5">
          <div className={`p-2 rounded-xl text-white shadow-md ${mode === 'register' ? 'bg-orange-500' : 'bg-slate-900'}`}>
            {mode === 'register' ? <User className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
          </div>
          <div>
            <h2 className="font-display font-bold text-lg text-slate-900">
              {mode === 'register' ? 'Create Your Catalog' : 'Welcome Back'}
            </h2>
            <p className="text-[11px] text-slate-400">
              {mode === 'register' ? 'Free to start, no card needed' : 'Sign in to your vendor dashboard'}
            </p>
          </div>
        </div>

        {/* ── Google button ── */}
        <button
          type="button"
          onClick={handleGoogleAuth}
          disabled={googleLoading || loading}
          className="w-full flex items-center justify-center gap-2.5 py-2.5 px-4 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-sm font-semibold text-slate-700 transition-all shadow-sm disabled:opacity-50 mb-4"
        >
          <GoogleIcon />
          {googleLoading ? 'Redirecting...' : `Continue with Google`}
        </button>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-[11px] text-slate-400 font-medium">or with email</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* ── Email/password form ── */}
        <form onSubmit={mode === 'register' ? handleRegister : handleLogin} className="space-y-3.5">
          {mode === 'register' && (
            <>
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">
                  Business Handle
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-xs text-slate-400 font-mono">vendorpulse/</span>
                  <input
                    type="text"
                    placeholder="e.g. aduaboutique"
                    value={businessName}
                    onChange={e => setBusinessName(e.target.value)}
                    className="w-full bg-white/60 border border-slate-200 rounded-xl pl-28 pr-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all font-mono"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  placeholder="e.g. 0244123456"
                  value={whatsapp}
                  onChange={e => setWhatsapp(e.target.value)}
                  className="w-full bg-white/60 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all font-mono"
                  required
                />
                <span className="text-[9px] text-slate-400 mt-1 block">
                  💡 0xx numbers auto-converted to 233xx international format.
                </span>
              </div>
            </>
          )}

          <div>
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">Email</label>
            <input
              type="email"
              placeholder="e.g. myboutique@gmail.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full bg-white/60 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
              required
            />
          </div>

          <div>
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">
              Password {mode === 'register' && <span className="text-slate-300">(min 6 chars)</span>}
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-white/60 border border-slate-200 rounded-xl px-3.5 py-2 pr-10 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                required
              />
              <button type="button" onClick={() => setShowPw(p => !p)} className="absolute right-3 top-2 text-slate-400 hover:text-slate-600">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-red-700 text-xs">⚠️ {error}</div>}
          {success && <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-emerald-700 text-xs">✅ {success}</div>}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 active:scale-95 text-white font-semibold text-xs rounded-xl uppercase tracking-wider transition-all shadow-lg shadow-orange-500/20"
          >
            {loading
              ? (mode === 'register' ? 'Creating...' : 'Signing in...')
              : (mode === 'register' ? 'Create Catalog' : 'Sign In')}
          </button>

          {mode === 'login' && (
            <button type="button" onClick={handleForgotPassword} disabled={loading}
              className="w-full text-center text-xs text-slate-400 hover:text-orange-500 transition-colors">
              Forgot password?
            </button>
          )}
        </form>

        <p className="text-center text-xs text-slate-400 mt-5">
          {mode === 'register' ? (
            <>Already have an account?{' '}
              <button onClick={() => { setMode('login'); setError(null); }} className="text-orange-500 font-semibold hover:underline">Sign in</button>
            </>
          ) : (
            <>Don't have an account?{' '}
              <button onClick={() => { setMode('register'); setError(null); }} className="text-orange-500 font-semibold hover:underline">Register</button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
